import React, { useState } from "react";
import {
  ModalBuilder,
  Form,
  Modal,
  getLookupData,
  Lookup,
} from "@redriver/cinnamon";
import { FloatingEditButton } from "components/buttons";
import {
  RegexInput,
  ProgrammeThemeDropdown,
  ProgrammeSubThemeDropdown,
  OrganisationsSubForm,
  OtherSocialMediaSubForm,
} from "components/forms";
import produce from "immer";
import { useSelector } from "react-redux";
import {
  programmeSubThemesLookup,
  programmeThemeLookup,
} from "modules/lookups";
import { SocialMediaLinkType } from "constants/enums";
import { socialMediaTypesLookup } from "modules/lookups";
import { Popup } from "semantic-ui-react";
import {
  socialMediaLinkRegexMapping,
  socialMediaLinkExampleMapping,
} from "constants/regexes";
import { InfoIcon } from "components/icons";
import ShouldUpdateFutureEventProfilesMessage from "./ShouldUpdateFutureEventProfilesMessage";

const EditProfileDetails = ({
  profile,
  setProfile,
  onOpened,
  onClosed,
  submitAction,
  submitParams,
  children,
  extraInitialData = {},
  displayPublicOrganisations = false,
  displayFutureProfileUpdateMessage,
  ...props
}) => {
  const [formData, setFormData] = useState(false);
  const initialData = {
    details: {
      displayName: profile?.displayName,
      organisations: profile?.organisations || [],
      themes: (profile?.themes || []).map((x) => x.value),
      subThemes: (profile?.subThemes || []).map((x) => x.value),
      subThemeNames: (profile?.subThemes || []).map((x) => x.text),
      linkedIn: profile?.linkedIn,
      otherSocialMedia: (profile?.otherSocialMedia || []).map((x) => ({
        ...x,
        type: x.socialMediaTypeId,
      })),
    },
    ...extraInitialData,
  };

  const availableThemes =
    useSelector((s) => getLookupData(s, programmeThemeLookup))?.response || [];
  const availableSubThemes =
    useSelector((s) => getLookupData(s, programmeSubThemesLookup))?.response ||
    [];

  return (
    <Lookup
      lookup={socialMediaTypesLookup}
      render={({ response }) => {
        const availableSocialMediaTypes = response || [];
        return (
          <ModalBuilder
            withForm
            initialData={initialData}
            renderTrigger={(openModal) => (
              <div className="edit-profile-details">
                <FloatingEditButton
                  onClick={() => {
                    openModal();
                    setFormData(initialData);
                    if (onOpened) onOpened();
                  }}
                  {...props}
                />
              </div>
            )}
            submitAction={submitAction}
            submitParams={submitParams}
            onSubmitted={(_, __, { data }) => {
              const nextProfile = produce(profile, (draft) => {
                draft.displayName = data.details.displayName;
                draft.organisations = data.details.organisations;
                draft.themes = data.details.themes
                  .map((x) => availableThemes.find((t) => t.value == x))
                  .filter((x) => !!x);

                draft.subThemes = data.details.subThemes
                  .map((x) => availableSubThemes.find((t) => t.value == x))
                  .filter((x) => !!x);
                let subThemeNamesToAdd = data.details.subThemeNames.filter(
                  (st) => !draft.subThemes.map((s) => s.text).includes(st)
                );
                draft.subThemes.push(
                  ...subThemeNamesToAdd.map((st) => ({
                    text: st,
                    value: st,
                  }))
                );

                draft.linkedIn = data.details.linkedIn;
                draft.otherSocialMedia = data.details.otherSocialMedia.map(
                  (x) => ({
                    ...x,
                    socialMediaTypeId: x.type,
                    type: availableSocialMediaTypes.find(
                      (a) => a.value == x.type
                    )?.type,
                  })
                );
              });
              setProfile(nextProfile);
              if (onClosed) onClosed();
            }}
            onCancelled={() => {
              if (onClosed) onClosed();
            }}
            renderModal={(modalProps, formProps) => (
              <Modal.Edit
                {...modalProps}
                header="Edit Profile Details"
                onClick={(ev) => ev.stopPropagation()}
              >
                <Form
                  {...formProps}
                  onChange={(field, data, applyChanges) => {
                    formProps.onChange(field, data, applyChanges);
                    setFormData(applyChanges(formProps.value));
                  }}
                >
                  <Form.Object field="details">
                    <Form.Group widths="2">
                      <Form.Input
                        field="displayName"
                        label={
                          <label>
                            Profile Display Name
                            <Popup
                              inverted
                              content="This is the name that will be displayed to other participants at events that you attend, and is normally your first name and last name. Your full name is also editable on your Account details."
                              trigger={<InfoIcon />}
                            />
                          </label>
                        }
                        fluid
                        required
                        placeholder="Enter name to display on your profile"
                      />
                    </Form.Group>

                    <OrganisationsSubForm
                      field="organisations"
                      label="Organisations"
                      showPublic={displayPublicOrganisations}
                      publicLabel="Available to others"
                      publicWidth={115}
                      organisationRequired
                      positionRequired
                      allowAdditions
                    />

                    <ProgrammeThemeDropdown
                      field="themes"
                      label="Themes"
                      fluid
                      multiple
                      clearable
                      placeholder="Select one or more themes you are interested in"
                      showSelectionInfo
                    />
                    <ProgrammeSubThemeDropdown
                      field="subThemes"
                      textField="subThemeNames"
                      label="Interests"
                      fluid
                      multiple
                      clearable
                      search
                      allowAdditions
                      placeholder="Select one or more interests"
                      showSelectionInfo
                    />
                    <Form.Group widths="two">
                      <RegexInput
                        field="linkedIn"
                        label="LinkedIn"
                        fluid
                        regexPattern={
                          socialMediaLinkRegexMapping[
                            SocialMediaLinkType.LinkedIn
                          ]
                        }
                        regexFlags="i"
                        placeholder="Enter your LinkedIn profile link"
                        regexError={`Link must match the format ${
                          socialMediaLinkExampleMapping[
                            SocialMediaLinkType.LinkedIn
                          ]
                        }`}
                      />
                    </Form.Group>

                    <OtherSocialMediaSubForm
                      field="otherSocialMedia"
                      label="Other Social Media Links"
                    />
                  </Form.Object>
                  {displayFutureProfileUpdateMessage && (
                    <ShouldUpdateFutureEventProfilesMessage />
                  )}
                  {children}
                </Form>
              </Modal.Edit>
            )}
          />
        );
      }}
    />
  );
};

export default EditProfileDetails;
